import preview from './access-preview.cjs';
import accessModel from './admin-access-model.cjs';
const { classifyAccessValue, formatAccessEventTime, summarizeBranchAccess } = accessModel;
const SESSION_KEY = 'wb-parts.admin-session';
const ROLE_REVALIDATION_MS = 45 * 60 * 1000;

function element(tag, text, className) {
  const node = document.createElement(tag);
  if (text !== undefined) node.textContent = text;
  if (className) node.className = className;
  return node;
}
function button(label, action, className = '') {
  const node = element('button', label, className); node.type = 'button'; node.addEventListener('click', action); return node;
}
function field(label, control) { const wrap = element('label', label, 'admin-field'); wrap.append(control); return wrap; }
function input(type, value = '') { const node = document.createElement('input'); node.type = type; node.value = value; return node; }
function select(values = []) { const node = document.createElement('select'); for (const [value, label] of values) { const option = element('option', label); option.value = value; node.append(option); } return node; }
function session() { try { const value = JSON.parse(sessionStorage.getItem(SESSION_KEY)); return value?.expiresAt * 1000 > Date.now() ? value : null; } catch { return null; } }

export async function renderAccessAdministration(app, authClient) {
  app.replaceChildren();
  const header = element('header', undefined, 'topbar admin-header');
  const headerInner = element('div', undefined, 'topbar-inner');
  const identity = element('div', undefined, 'portal-identity');
  const home = element('a', undefined, 'admin-brand'); home.href = '/parts-escalation';
  home.setAttribute('aria-label', 'W&B Parts Escalation home');
  const logo = element('img', undefined, 'portal-logo'); logo.src = '/assets/branding/favicon.png'; logo.alt = 'W&B'; home.append(logo);
  const sessionControls = element('div', undefined, 'portal-account');
  const title = element('div', undefined, 'admin-header-title');
  const environment = authClient.config?.environment;
  const environmentLabel = environment === 'production' ? 'PROD' : environment === 'test' ? 'TEST' : 'Environment unavailable';
  const environmentBadge = element('span', environmentLabel, 'admin-environment');
  if (environment === 'production' || environment === 'test') environmentBadge.dataset.environment = environment;
  environmentBadge.setAttribute('aria-label', `Access environment: ${environmentLabel}`);
  const heading = element('h1');
  heading.append('Parts Escalation ', element('span', '/'), ' ', element('strong', 'Admin'));
  title.append(heading, environmentBadge);
  identity.append(home, title); headerInner.append(identity); header.append(headerInner);
  app.append(header);
  const status = element('p', '', 'admin-status'); status.setAttribute('role', 'status'); app.append(status);
  const body = element('section', undefined, 'admin-content'); app.append(body);
  let expiryTimer = null, revalidationTimer = null, accountMenuAbortController = null;
  function clearSessionTimers() {
    if (expiryTimer) clearTimeout(expiryTimer);
    if (revalidationTimer) clearTimeout(revalidationTimer);
    expiryTimer = null; revalidationTimer = null;
  }
  function signedOut(message = 'Sign in with your assigned W&B Microsoft administrator account.') {
    clearSessionTimers(); accountMenuAbortController?.abort(); accountMenuAbortController = null;
    authClient.endAdministratorSession(); sessionControls.remove(); body.replaceChildren(); status.textContent = message;
    body.append(button('Sign in with Microsoft', async () => { try { await authClient.signInAsAdministrator(); } catch (error) { status.textContent = error.message; } }));
  }
  let currentSession = session();
  if (!currentSession) {
    const message = sessionStorage.getItem('wb-parts.admin-error');
    sessionStorage.removeItem('wb-parts.admin-error');
    if (message) { signedOut(message); return; }
    status.textContent = 'Checking your Microsoft session…';
    currentSession = await authClient.restoreAdministratorSession();
    status.textContent = '';
    if (!currentSession) { signedOut(); return; }
  }
  const accountEmail = element('button', currentSession.user.email, 'account-email'); accountEmail.type = 'button'; accountEmail.title = 'Signed-in account';
  const accountMenuToggle = element('button', undefined, 'account-menu-toggle'); accountMenuToggle.type = 'button';
  accountMenuToggle.setAttribute('aria-label', 'Account options'); accountMenuToggle.setAttribute('aria-expanded', 'false'); accountMenuToggle.setAttribute('aria-controls', 'admin-account-menu');
  accountMenuToggle.append(element('i'), element('i'), element('i'));
  const accountMenu = element('div', undefined, 'portal-account-menu'); accountMenu.id = 'admin-account-menu'; accountMenu.hidden = true;
  accountMenu.append(button('Sign out', () => signedOut()), element('p', 'Version 0.4.3', 'account-menu-version'));
  sessionControls.append(accountEmail, accountMenuToggle, accountMenu);
  headerInner.append(sessionControls);
  accountMenuAbortController = new AbortController();
  const accountMenuSignal = accountMenuAbortController.signal;
  function closeAccountMenu() { accountMenu.hidden = true; accountMenuToggle.setAttribute('aria-expanded', 'false'); }
  accountMenuToggle.addEventListener('click', event => {
    event.stopPropagation();
    const willOpen = accountMenu.hidden;
    accountMenu.hidden = !willOpen;
    accountMenuToggle.setAttribute('aria-expanded', String(willOpen));
  }, { signal: accountMenuSignal });
  accountMenu.addEventListener('click', event => event.stopPropagation(), { signal: accountMenuSignal });
  document.addEventListener('click', closeAccountMenu, { signal: accountMenuSignal });
  document.addEventListener('keydown', event => { if (event.key === 'Escape') closeAccountMenu(); }, { signal: accountMenuSignal });
  function scheduleSessionTimers(activeSession) {
    clearSessionTimers();
    const now = Date.now();
    const expiresAt = activeSession.expiresAt * 1000;
    expiryTimer = setTimeout(() => signedOut('Your administrator work session ended. Sign in again to continue.'), Math.max(0, expiresAt - now));
    const workSessionExpiresAt = Number(activeSession.workSessionExpiresAt) * 1000;
    if (Number.isFinite(workSessionExpiresAt) && expiresAt >= workSessionExpiresAt) return;
    const validatedAt = Number(activeSession.validatedAt) * 1000 || now;
    const revalidateAt = Math.min(validatedAt + ROLE_REVALIDATION_MS, expiresAt - 60_000);
    revalidationTimer = setTimeout(async () => {
      const refreshed = await authClient.refreshAdministratorSession();
      if (!refreshed) { signedOut('Your Microsoft administrator session could not be renewed. Sign in again to continue.'); return; }
      scheduleSessionTimers(refreshed);
    }, Math.max(1000, revalidateAt - now));
  }
  scheduleSessionTimers(currentSession);
  window.addEventListener('pagehide', () => { clearSessionTimers(); accountMenuAbortController?.abort(); }, { once: true });

  let companies = [], policies = {}, auditEvents = {}, auditCursors = {}, pending = null, busy = false;
  async function api(path, method = 'GET', data) {
    const current = session();
    if (!current) { signedOut('Your administrator session expired. Sign in again.'); throw new Error('Administrator sign-in required.'); }
    const response = await fetch(`/api/access-admin/${path}`, { method, cache: 'no-store', headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-WB-Admin-Token': `Bearer ${current.sessionToken}` }, ...(data ? { body: JSON.stringify(data) } : {}) });
    const result = await response.json().catch(() => ({}));
    if (response.status === 401) signedOut(result.message);
    if (!response.ok) { const error = new Error(result.message || 'Access administration is unavailable.'); error.status = response.status; throw error; }
    return result;
  }
  async function perform(work) {
    if (busy) return;
    busy = true; body.setAttribute('aria-busy', 'true');
    try { await work(); } catch (error) { status.textContent = error.message; }
    finally { busy = false; body.removeAttribute('aria-busy'); }
  }

  const retry = button('Retry pending change', () => perform(() => save(pending))); retry.hidden = true;
  const accessPanel = element('section', undefined, 'admin-panel admin-branch-panel');
  accessPanel.setAttribute('aria-label', 'Branch access');
  const branchSelect = select();
  branchSelect.hidden = true;
  const branchPicker = element('div', undefined, 'admin-branch-picker');
  const branchCombobox = element('div', undefined, 'admin-branch-combobox');
  const branchSearch = input('text'); branchSearch.className = 'admin-branch-search'; branchSearch.autocomplete = 'off'; branchSearch.spellcheck = false;
  branchSearch.setAttribute('role', 'combobox'); branchSearch.setAttribute('aria-label', 'Company'); branchSearch.setAttribute('aria-autocomplete', 'list');
  branchSearch.setAttribute('aria-expanded', 'false'); branchSearch.setAttribute('aria-controls', 'admin-branch-options');
  const branchToggle = element('button', undefined, 'admin-branch-toggle'); branchToggle.type = 'button'; branchToggle.setAttribute('aria-label', 'Show companies');
  branchToggle.append(element('span', undefined, 'admin-branch-chevron'));
  const branchOptions = element('div', undefined, 'admin-branch-options'); branchOptions.id = 'admin-branch-options'; branchOptions.setAttribute('role', 'listbox'); branchOptions.hidden = true;
  branchCombobox.append(branchSearch, branchToggle, branchOptions); branchPicker.append(branchSelect, branchCombobox);
  const branchNotice = element('p', '', 'admin-access-notice'); branchNotice.hidden = true;
  accessPanel.append(branchPicker, branchNotice);

  let visibleCompanies = [], activeBranchIndex = -1;
  function closeBranchOptions() {
    branchOptions.hidden = true; activeBranchIndex = -1;
    branchSearch.removeAttribute('aria-activedescendant'); branchSearch.setAttribute('aria-expanded', 'false');
    branchSearch.value = companyName(branchSelect.value);
  }
  function renderBranchOptions(query = '') {
    const search = query.trim().toLocaleLowerCase();
    visibleCompanies = companies.filter(company => company.displayName.toLocaleLowerCase().includes(search));
    branchOptions.replaceChildren(); activeBranchIndex = -1;
    if (!visibleCompanies.length) {
      const empty = element('p', 'No companies found', 'admin-branch-empty'); empty.setAttribute('role', 'status'); branchOptions.append(empty); return;
    }
    for (const company of visibleCompanies) {
      const option = element('div', company.displayName, 'admin-branch-option'); option.id = `admin-branch-option-${company.slug}`; option.setAttribute('role', 'option'); option.tabIndex = -1;
      option.setAttribute('aria-selected', String(company.slug === branchSelect.value));
      option.addEventListener('pointerdown', event => event.preventDefault());
      option.addEventListener('click', () => selectBranch(company.slug));
      branchOptions.append(option);
    }
  }
  function openBranchOptions() {
    if (branchSearch.disabled) return;
    renderBranchOptions(); branchOptions.hidden = false; branchSearch.setAttribute('aria-expanded', 'true');
  }
  function selectBranch(slug) {
    if (slug === branchSelect.value) { closeBranchOptions(); branchSearch.focus(); return; }
    branchSelect.value = slug; closeBranchOptions(); branchSelect.dispatchEvent(new Event('change', { bubbles: true })); branchSearch.focus();
  }
  function setActiveBranch(index) {
    if (!visibleCompanies.length) return;
    activeBranchIndex = (index + visibleCompanies.length) % visibleCompanies.length;
    const active = branchOptions.children[activeBranchIndex];
    branchSearch.setAttribute('aria-activedescendant', active.id); active.scrollIntoView({ block: 'nearest' });
    for (const option of branchOptions.children) option.classList.toggle('is-active', option === active);
  }
  branchSearch.addEventListener('focus', () => { openBranchOptions(); branchSearch.select(); });
  branchSearch.addEventListener('input', () => { if (branchOptions.hidden) openBranchOptions(); renderBranchOptions(branchSearch.value); });
  branchSearch.addEventListener('keydown', event => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault(); if (branchOptions.hidden) openBranchOptions(); setActiveBranch(activeBranchIndex + (event.key === 'ArrowDown' ? 1 : -1));
    } else if (event.key === 'Enter' && !branchOptions.hidden) {
      event.preventDefault();
      const exact = visibleCompanies.find(company => company.displayName.toLocaleLowerCase() === branchSearch.value.trim().toLocaleLowerCase());
      if (activeBranchIndex >= 0) selectBranch(visibleCompanies[activeBranchIndex].slug); else if (exact) selectBranch(exact.slug);
    } else if (event.key === 'Escape') { event.preventDefault(); closeBranchOptions(); branchSearch.select(); }
  });
  branchToggle.addEventListener('pointerdown', event => event.preventDefault());
  branchToggle.addEventListener('click', () => {
    if (branchOptions.hidden) { branchSearch.focus(); openBranchOptions(); branchSearch.select(); }
    else { closeBranchOptions(); branchSearch.focus(); }
  });
  branchPicker.addEventListener('focusout', () => setTimeout(() => { if (!branchPicker.contains(document.activeElement)) closeBranchOptions(); }));

  function accessGroup(title, type, buttonLabel) {
    const section = element('section', undefined, 'admin-access-group');
    const heading = element('div', undefined, 'admin-access-group-heading');
    const headingText = element('div'); headingText.append(element('h3', title));
    const rows = element('div', undefined, 'admin-branch-access-list');
    rows.id = `admin-${type}-list`;
    const edit = button('Edit', () => {
      const editing = section.classList.toggle('is-editing');
      edit.textContent = editing ? 'Done' : 'Edit';
      edit.setAttribute('aria-label', `${editing ? 'Finish editing' : 'Edit'} ${title.toLowerCase()}`);
      edit.setAttribute('aria-expanded', String(editing));
    }, 'admin-edit-access');
    edit.setAttribute('aria-label', `Edit ${title.toLowerCase()}`);
    edit.setAttribute('aria-expanded', 'false'); edit.setAttribute('aria-controls', rows.id);
    heading.append(headingText, edit);
    const form = document.createElement('form'); form.className = 'admin-access-add';
    form.id = `admin-${type}-add`; form.hidden = true;
    const value = input(type); value.maxLength = 254; value.required = true;
    value.setAttribute('aria-label', type === 'email' ? 'New email address' : 'New domain');
    const submit = element('button', buttonLabel); submit.type = 'submit';
    const add = button(buttonLabel, () => {
      form.hidden = false; add.hidden = true; value.focus();
    });
    const cancel = button('Cancel', () => {
      form.hidden = true; add.hidden = false; value.value = ''; value.setCustomValidity(''); add.focus();
    }, 'admin-cancel-add');
    const actions = element('div', undefined, 'admin-access-actions'); actions.append(add);
    form.append(value, submit, cancel); section.append(heading, rows, actions, form);
    return { section, rows, form, value, submit, add, edit, cancel, title };
  }
  const domainGroup = accessGroup('Domains', 'text', 'Add domain');
  const emailGroup = accessGroup('Individual users', 'email', 'Add email');
  accessPanel.append(domainGroup.section, emailGroup.section);


  const auditPanel = element('section', undefined, 'admin-panel admin-audit-panel'), auditRows = element('div', undefined, 'admin-audit-list');
  const moreAudit = button('Load older changes', () => perform(loadMoreAudit));
  auditPanel.append(element('h2', 'Activity Log'), auditRows, moreAudit);

  const removalDialog = element('dialog', undefined, 'admin-confirm-dialog');
  const removalCard = element('div', undefined, 'admin-confirm-card');
  const removalDescription = element('p', '', 'admin-confirm-description'); removalDescription.id = 'admin-remove-access-description';
  const removalActions = document.createElement('form'); removalActions.method = 'dialog'; removalActions.className = 'admin-confirm-actions';
  const keepAccess = element('button', 'Cancel', 'admin-confirm-cancel'); keepAccess.type = 'submit'; keepAccess.value = 'cancel';
  const confirmRemovalButton = element('button', 'Remove', 'admin-confirm-remove'); confirmRemovalButton.type = 'submit'; confirmRemovalButton.value = 'confirm';
  removalActions.append(keepAccess, confirmRemovalButton);
  removalCard.append(removalDescription, removalActions); removalDialog.append(removalCard);
  removalDialog.setAttribute('aria-labelledby', removalDescription.id);

  const loading = element('div', undefined, 'admin-loading'); loading.hidden = true; loading.setAttribute('role', 'status');
  const spinner = element('span', undefined, 'admin-spinner'); spinner.setAttribute('aria-hidden', 'true');
  loading.append(spinner, element('span', 'Loading access rules…'));
  const retryLoad = button('Retry loading access rules', () => perform(load)); retryLoad.hidden = true;
  accessPanel.hidden = true; auditPanel.hidden = true;
  body.append(loading, retryLoad, retry, accessPanel, auditPanel, removalDialog);

  function populateCompanySelect(control) {
    const selected = control.value;
    control.replaceChildren();
    for (const company of companies) {
      const option = element('option', company.displayName); option.value = company.slug; control.append(option);
    }
    if (companies.some(company => company.slug === selected)) control.value = selected;
    branchSearch.value = companyName(control.value);
    renderBranchOptions();
  }
  function companyName(slug) { return companies.find(company => company.slug === slug)?.displayName || slug; }
  function mutation(company, rule, changes, reason) {
    return {
      path: `access-rules${rule ? `/${rule.ruleId}` : ''}`,
      method: rule ? 'PUT' : 'POST',
      body: {
        company,
        operationId: crypto.randomUUID(),
        expectedRevision: rule?.revision || 0,
        ruleType: changes.ruleType ?? rule.ruleType,
        value: changes.value ?? rule.value,
        effect: changes.effect ?? rule.effect,
        enabled: changes.enabled ?? rule.enabled,
        expiresAtUtc: changes.expiresAtUtc === undefined ? rule?.expiresAtUtc || null : changes.expiresAtUtc,
        reason
      }
    };
  }
  function renderAccess() {
    const company = branchSelect.value;
    const summary = summarizeBranchAccess(policies[company] || []);
    branchNotice.hidden = summary.enabled;
    branchNotice.textContent = summary.enabled ? '' : `${companyName(company)} is currently unavailable.`;
    renderAccessGroup(domainGroup.rows, summary.domains, 'No domains have access to this branch.');
    renderAccessGroup(emailGroup.rows, summary.emails, 'No individual email addresses have access to this branch.');
    for (const control of [branchSelect, branchSearch, branchToggle, ...[domainGroup, emailGroup].flatMap(group => [group.value, group.submit, group.add, group.edit, group.cancel])]) control.disabled = Boolean(pending);
  }
  function renderAccessGroup(container, rules, emptyMessage) {
    container.replaceChildren();
    if (!rules.length) { container.append(element('p', emptyMessage, 'admin-empty')); return; }
    for (const rule of rules) {
      const row = element('div', undefined, 'admin-branch-access-row');
      row.append(element('span', rule.ruleType === 'domain' ? `@${rule.value}` : rule.value));
      const remove = button('Remove', () => removeAccess(branchSelect.value, rule), 'admin-remove-access');
      remove.disabled = Boolean(pending); row.append(remove); container.append(row);
    }
  }

  function renderAudit() {
    auditRows.replaceChildren();
    const auditHeader = element('div', undefined, 'admin-audit-header');
    auditHeader.append(element('span', 'Time'), element('span', 'Event'));
    auditRows.append(auditHeader);
    const company = branchSelect.value;
    const events = (auditEvents[company] || []).slice().sort((left, right) => Date.parse(right.timestampUtc) - Date.parse(left.timestampUtc));
    if (!events.length) auditRows.append(element('p', 'No access changes have been recorded yet.'));
    for (const event of events) {
      const target = event.after.value ? `${event.after.ruleType === 'domain' ? '@' : ''}${event.after.value}` : 'company access';
      const verb = event.outcome !== 'committed' ? 'attempted a change for' : !event.after.enabled ? 'removed access for' : event.after.effect === 'deny' ? 'blocked' : 'granted access to';
      const row = element('div', undefined, 'admin-audit-row');
      const timestamp = element('time', formatAccessEventTime(event.timestampUtc)); timestamp.dateTime = event.timestampUtc;
      const change = element('span', undefined, 'admin-audit-change');
      change.append(element('strong', event.actor.email), ` ${verb} `, element('strong', target));
      row.append(timestamp, change);
      auditRows.append(row);
    }
    moreAudit.hidden = !auditCursors[company];
  }
  async function loadAuditPage(company, reset) {
    if (reset) { auditEvents[company] = []; auditCursors[company] = null; }
    const cursor = reset ? '' : auditCursors[company];
    if (!reset && !cursor) return;
    const result = await api(`access-audit?company=${encodeURIComponent(company)}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`);
    auditEvents[company].push(...result.events); auditCursors[company] = result.cursor;
  }
  async function loadMoreAudit() { await loadAuditPage(branchSelect.value, false); renderAudit(); }
  async function load() {
    loading.hidden = false; retryLoad.hidden = true; accessPanel.hidden = true; auditPanel.hidden = true;
    status.textContent = '';
    try {
      const first = await api('access-rules?company=sysco');
      companies = first.companies; policies = { sysco: first.rules };
      await Promise.all(companies.filter(company => company.slug !== 'sysco').map(async company => { policies[company.slug] = (await api(`access-rules?company=${encodeURIComponent(company.slug)}`)).rules; }));
      populateCompanySelect(branchSelect);
      auditEvents = {}; auditCursors = {};
      await Promise.all(companies.map(company => loadAuditPage(company.slug, true)));
      renderAccess(); renderAudit();
      accessPanel.hidden = false; auditPanel.hidden = false;
    } catch (error) {
      retryLoad.hidden = false; throw error;
    } finally { loading.hidden = true; }
  }
  async function save(change) {
    if (!change) return;
    try {
      await api(change.path, change.method, change.body);
      pending = null; retry.hidden = true; await load();
      status.textContent = change.success;
    } catch (error) {
      if (error.status && error.status < 500) { pending = null; retry.hidden = true; if (session()) await load(); }
      else { pending = change; retry.hidden = false; renderAccess(); }
      throw error;
    }
  }
  function confirmRemoval(company, rule) {
    const target = `${rule.ruleType === 'domain' ? '@' : ''}${rule.value}`;
    removalDescription.replaceChildren('Are you sure you want to remove ', element('strong', target), ` from ${companyName(company)} Form?`);
    removalDialog.returnValue = '';
    return new Promise(resolve => {
      removalDialog.addEventListener('close', () => resolve(removalDialog.returnValue === 'confirm'), { once: true });
      removalDialog.showModal();
    });
  }
  async function removeAccess(company, rule) {
    if (!await confirmRemoval(company, rule)) return;
    await perform(async () => {
      pending = { ...mutation(company, rule, { enabled: false }, 'Access removed in the administration portal.'), success: `${rule.value} was removed from ${companyName(company)}.` };
      await save(pending);
      if (rule.ruleType === 'email' && preview.previewAccess(rule.value, policies[company] || []).startsWith('Allowed')) status.textContent = `${rule.value} still has access to ${companyName(company)} through a domain entry.`;
    });
  }
  function handleAdd(group, expectedType) {
    group.value.addEventListener('input', () => group.value.setCustomValidity(''));
    group.form.addEventListener('submit', event => {
      event.preventDefault();
      if (busy) return;
      const target = classifyAccessValue(group.value.value);
      if (!target || target.ruleType !== expectedType) {
        group.value.setCustomValidity(expectedType === 'domain' ? 'Enter a domain such as company.com.' : 'Enter a complete email address.');
        group.value.reportValidity(); return;
      }
      perform(async () => {
        const company = branchSelect.value;
        const existing = (policies[company] || []).find(rule => rule.ruleType === target.ruleType && rule.value === target.value);
        pending = { ...mutation(company, existing, { ...target, effect: 'allow', enabled: true, expiresAtUtc: null }, 'Access granted in the administration portal.'), success: `${target.value} now has access to ${companyName(company)}.` };
        await save(pending); group.value.value = ''; group.form.hidden = true; group.add.hidden = false; group.add.focus();
      });
    });
  }
  handleAdd(domainGroup, 'domain');
  handleAdd(emailGroup, 'email');
  branchSelect.addEventListener('change', () => {
    status.textContent = '';
    for (const group of [domainGroup, emailGroup]) {
      group.section.classList.remove('is-editing'); group.edit.textContent = 'Edit'; group.edit.setAttribute('aria-expanded', 'false');
      group.edit.setAttribute('aria-label', `Edit ${group.title.toLowerCase()}`);
      group.form.hidden = true; group.add.hidden = false; group.value.value = ''; group.value.setCustomValidity('');
    }
    renderAccess(); renderAudit();
  });

  await perform(load);
}
